/**
 * PRODUCT NAVIGATION DROPDOWN
 * ---------------------------------------------------------------------------------------------------------------------
 * Custom element used inside the native <x-popover> dropdown (see snippets/product-nav-dropdown.liquid). It does NOT
 * handle open/close — that is inherited from the theme's <x-popover> (assets/theme.js). It only adds:
 *   - prefetch-on-intent: pre-caches a target product page on hover / focus / touch (once per URL, respects Save-Data)
 *   - listbox-style ArrowUp/ArrowDown/Home/End navigation between the option links, mirroring the native dropdown
 */
if (!customElements.get('product-nav-dropdown')) {
  class ProductNavDropdown extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const { signal } = this._abortController;

      this._links = Array.from(this.querySelectorAll('a[data-nav-link]'));

      this._links.forEach((link) => {
        const onIntent = () => this._prefetch(link.href);
        link.addEventListener('mouseenter', onIntent, { signal });
        link.addEventListener('focus', onIntent, { signal });
        link.addEventListener('touchstart', onIntent, { passive: true, signal });
      });

      this.addEventListener('keydown', this._onKeyDown.bind(this), { signal });
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    _prefetch(href) {
      if (!href) return;
      if (navigator.connection && navigator.connection.saveData) return;
      if (ProductNavDropdown._prefetched.has(href)) return;
      ProductNavDropdown._prefetched.add(href);

      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      link.as = 'document';
      document.head.appendChild(link);
    }

    _onKeyDown(event) {
      const links = this._links;
      if (!links || links.length === 0) return;

      const index = links.indexOf(document.activeElement);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          (links[index + 1] || links[0]).focus();
          break;
        case 'ArrowUp':
          event.preventDefault();
          (links[index - 1] || links[links.length - 1]).focus();
          break;
        case 'Home':
          event.preventDefault();
          links[0].focus();
          break;
        case 'End':
          event.preventDefault();
          links[links.length - 1].focus();
          break;
      }
    }
  }

  ProductNavDropdown._prefetched = new Set();
  customElements.define('product-nav-dropdown', ProductNavDropdown);
}
